import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  CardBody,
  Button,
  Label,
  Input,
  Container,
  Col,
  Row,
  Card,
  CardHeader,
  Spinner,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Select from "react-select";
import countriesData from "../../common/country.json";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useProfile } from "../../Components/Hooks/UserHooks";
import {
  getAvailableContacts as onGetAvailableContacts,
  getClientDetails as onGetClientDetails,
  updateClient as onUpdateClient,
} from "../../slices/thunks";
import { createSelector } from "reselect";
import ClientContracts from "./ClientContracts";
import ContractModal from "../Contract/ContractModal/ContractModal";

// Selectors
const selectClientState = (state) => state.Clients;
const selectClientProperties = createSelector(selectClientState, (client) => ({
  availableContacts: client.availableContacts,
  clientInfo: client.clientInfo,
  contracts: client.contracts,
  additionalContact: client.additionalContact,
  loading: client.loading,
  error: client.error,
  isClientSuccess: client.isClientSuccess,
}));

// Constants
const TABS = {
  PERSONAL_INFO: "1",
  CONTACT: "2",
  ADDRESS: "3",
  BANKING: "4",
  ADDITIONAL_CONTACT: "5",
};

const BUYER_TYPES = {
  NATURAL: "NATURAL",
  LEGAL: "LEGAL",
};

const ClientDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Selectors
  const {
    availableContacts,
    clientInfo,
    contracts,
    additionalContact,
    loading,
    error,
  } = useSelector(selectClientProperties);

  const { userProfile, loading: profileLoading } = useProfile();

  // Local state
  const [contractModal, setContractModal] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.PERSONAL_INFO);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [optimisticAdditionalContact, setOptimisticAdditionalContact] =
    useState(null);
  const [isContactOperationPending, setIsContactOperationPending] =
    useState(false);

  const displayAdditionalContact =
    optimisticAdditionalContact !== null
      ? optimisticAdditionalContact
      : additionalContact;

  const isLoadingDifferentClient =
    (loading && isInitialLoad) || id !== currentClientId;

  const countries = countriesData.map((country) => ({
    label: `${country.flag} ${country.name}`,
    value: country.name,
  }));

  const isViewer = userProfile?.user?.groups?.includes("Viewer") || false;

  const isNaturalPerson = clientInfo?.buyer_type === BUYER_TYPES.NATURAL;

  const clientDisplayName = !clientInfo
    ? ""
    : isNaturalPerson
    ? `${clientInfo.name || ""} ${clientInfo.surname || ""}`.trim()
    : clientInfo.legal_company_name || "";

  const clientSubtitle = !clientInfo
    ? ""
    : isNaturalPerson
    ? clientInfo.personal_email || "No email provided"
    : clientInfo.legal_unique_registration_code ||
      "No registration code provided";

  // Effects
  useEffect(() => {
    if (id && id !== currentClientId) {
      setIsInitialLoad(true);
      dispatch(onGetClientDetails(id));
      setCurrentClientId(id);
    }
  }, [id, dispatch, currentClientId]);

  useEffect(() => {
    if (clientInfo && !loading && !error && id === currentClientId) {
      setFormData(clientInfo);
      setIsEditing(false);
      setIsInitialLoad(false);

      if (!isContactOperationPending) {
        setOptimisticAdditionalContact(null);
      }
    }
  }, [
    clientInfo,
    loading,
    error,
    id,
    currentClientId,
    isContactOperationPending,
  ]);

  useEffect(() => {
    const fetchAvailableClients = async () => {
      try {
        await dispatch(onGetAvailableContacts(id)).unwrap();
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    };

    if (id) {
      fetchAvailableClients(id);
    }
  }, [dispatch, id]);

  // Event handlers
  const toggleTab = useCallback(
    (tab) => {
      if (activeTab !== tab) {
        setActiveTab(tab);
      }
    },
    [activeTab]
  );

  const toggleEdit = useCallback(() => {
    if (isEditing) {
      setFormData(clientInfo);
    }
    setIsEditing(!isEditing);
  }, [isEditing, clientInfo]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  const handleCountryChange = useCallback(
    (selectedOption, fieldName = "country") => {
      setFormData((prevData) => ({
        ...prevData,
        [fieldName]: selectedOption.value,
      }));
    },
    []
  );

  const handleSave = useCallback(async () => {
    try {
      await dispatch(onUpdateClient(formData)).unwrap();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating client:", error);
    }
  }, [dispatch, formData]);

  const handleContractModal = useCallback(() => {
    setContractModal(true);
  }, []);

  const handleCloseContractModal = useCallback(() => {
    setContractModal(false);
  }, []);

  const toggleUnlinkModal = useCallback(() => {
    setUnlinkModalOpen(!unlinkModalOpen);
  }, [unlinkModalOpen]);

  const handleUnlinkAdditionalContact = useCallback(async () => {
    setIsContactOperationPending(true);

    setOptimisticAdditionalContact(null);
    setUnlinkModalOpen(false);

    try {
      const updatedFormData = {
        ...formData,
        additional_contact1: null,
      };

      await dispatch(onUpdateClient(updatedFormData)).unwrap();

      await dispatch(onGetClientDetails(id)).unwrap();

      setFormData(updatedFormData);
    } catch (error) {
      console.error("Error unlinking contact:", error);

      setOptimisticAdditionalContact(additionalContact);
    } finally {
      setIsContactOperationPending(false);
    }
  }, [dispatch, formData, additionalContact, id]);

  const handleAdditionalContactClick = useCallback(() => {
    if (displayAdditionalContact?.id) {
      navigate(`/client-details/${displayAdditionalContact.id}`);
    }
  }, [navigate, displayAdditionalContact?.id]);

  const handleAddExistingContact = useCallback(async () => {
    setIsContactOperationPending(true);

    const selectedContactId = formData?.additional_contact1;
    const selectedContact = availableContacts.find(
      (c) => c.id == selectedContactId
    );

    if (selectedContact) {
      setOptimisticAdditionalContact({
        id: selectedContact.id,
        name: selectedContact.name,
        surname: selectedContact.surname,
        legal_company_name: selectedContact.legal_company_name,
        buyer_type: selectedContact.buyer_type,
        personal_email: selectedContact.personal_email,
        business_email: selectedContact.business_email,
        mobile_phone_number: selectedContact.mobile_phone_number,
      });
    }

    try {
      await dispatch(onUpdateClient(formData)).unwrap();
      await dispatch(onGetClientDetails(id)).unwrap();
    } catch (error) {
      console.error("Error linking contact:", error);

      setOptimisticAdditionalContact(null);
    } finally {
      setIsContactOperationPending(false);
    }
  }, [dispatch, formData, availableContacts, id]);

  const renderFormField = useCallback(
    (fieldConfig) => {
      const {
        id,
        name,
        label,
        type = "text",
        icon,
        required = false,
        rows = null,
        options = null,
        onChange = null,
      } = fieldConfig;

      const baseProps = {
        id,
        name,
        value: formData?.[name] || "",
        onChange: onChange || handleInputChange,
        disabled: !isEditing,
        className: isEditing ? "border-primary" : "",
      };

      return (
        <div className="mb-3">
          <Label for={id}>
            {icon && <i className={`${icon} me-1`}></i>}
            {label}
            {required && <span className="text-danger">*</span>}
          </Label>
          {type === "select" && options ? (
            <Input type="select" {...baseProps}>
              <option value="">Select {label}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          ) : type === "country" ? (
            <Select
              options={countries}
              name={name}
              placeholder={`Select ${label}`}
              classNamePrefix="react-select"
              onChange={(selected) => handleCountryChange(selected, name)}
              value={countries.find(
                (option) => option.value === formData?.[name]
              )}
              isDisabled={!isEditing}
            />
          ) : type === "textarea" ? (
            <Input type="textarea" rows={rows || 3} {...baseProps} />
          ) : (
            <Input type={type} {...baseProps} />
          )}
        </div>
      );
    },
    [formData, isEditing, handleInputChange, handleCountryChange, countries]
  );

  const renderNaturalPersonForm = useCallback(() => {
    const personalFields = [
      { id: "name", name: "name", label: "First Name", required: true },
      { id: "surname", name: "surname", label: "Last Name", required: true },
      { id: "buyer_type", name: "buyer_type", label: "Buyer Type" },
      { id: "profession", name: "profession", label: "Profession" },
      {
        id: "social_security_number",
        name: "social_security_number",
        label: "Social Security Number",
      },
      {
        id: "social_security_series",
        name: "social_security_series",
        label: "Social Security Series",
      },
      { id: "passport", name: "passport", label: "Passport" },
    ];

    const contactFields = [
      {
        id: "personal_email",
        name: "personal_email",
        label: "Email Address",
        type: "email",
        icon: "ri-mail-line",
      },
      {
        id: "mobile_phone_number",
        name: "mobile_phone_number",
        label: "Mobile Phone",
        icon: "ri-smartphone-line",
      },
      {
        id: "land_phone_number",
        name: "land_phone_number",
        label: "Landline Phone",
        icon: "ri-phone-line",
      },
    ];

    const addressFields = [
      { id: "country", name: "country", label: "Country", type: "country" },
      { id: "region", name: "region", label: "Region" },
      { id: "city", name: "city", label: "City" },
      { id: "postal_code", name: "postal_code", label: "Postal Code" },
      { id: "address", name: "address", label: "Address", type: "textarea" },
    ];

    const bankingFields = [
      {
        id: "iban_ron",
        name: "iban_ron",
        label: "IBAN (RON)",
        icon: "ri-bank-card-line",
      },
      {
        id: "iban_euro",
        name: "iban_euro",
        label: "IBAN (EUR)",
        icon: "ri-bank-card-line",
      },
    ];

    const getFieldsForTab = () => {
      switch (activeTab) {
        case TABS.PERSONAL_INFO:
          return personalFields;
        case TABS.CONTACT:
          return contactFields;
        case TABS.ADDRESS:
          return addressFields;
        case TABS.BANKING:
          return bankingFields;
        case TABS.ADDITIONAL_CONTACT:
          return null;
        default:
          return [];
      }
    };

    const fields = getFieldsForTab();

    if (activeTab === TABS.ADDITIONAL_CONTACT) {
      return renderAdditionalContactTab();
    }

    return (
      <Row>
        {fields?.map((field, index) => (
          <Col md={field.type === "textarea" ? 12 : 6} key={field.id}>
            {renderFormField(field)}
          </Col>
        ))}
      </Row>
    );
  }, [activeTab, renderFormField]);

  const renderLegalEntityForm = useCallback(() => {
    const companyFields = [
      {
        id: "legal_company_name",
        name: "legal_company_name",
        label: "Company Name",
        required: true,
      },
      {
        id: "legal_fiscal_code",
        name: "legal_fiscal_code",
        label: "Fiscal Code",
      },
      {
        id: "legal_unique_registration_code",
        name: "legal_unique_registration_code",
        label: "Unique Registration Code",
      },
      {
        id: "legal_trade_register_number",
        name: "legal_trade_register_number",
        label: "Trade Register Number",
      },
    ];

    const contactFields = [
      {
        id: "personal_email",
        name: "personal_email",
        label: "Email Address",
        type: "email",
        icon: "ri-mail-line",
      },
      {
        id: "business_email",
        name: "business_email",
        label: "Business Email",
        type: "email",
        icon: "ri-mail-line",
      },
      {
        id: "mobile_phone_number",
        name: "mobile_phone_number",
        label: "Mobile Phone",
        icon: "ri-smartphone-line",
      },
      {
        id: "land_phone_number",
        name: "land_phone_number",
        label: "Landline Phone",
        icon: "ri-phone-line",
      },
    ];

    const addressFields = [
      {
        id: "legal_country",
        name: "legal_country",
        label: "Country",
        type: "country",
      },
      { id: "legal_region", name: "legal_region", label: "Region" },
      { id: "legal_city", name: "legal_city", label: "City" },
      {
        id: "legal_office_address",
        name: "legal_office_address",
        label: "Office Address",
        type: "textarea",
      },
    ];

    const bankingFields = [
      {
        id: "iban_ron",
        name: "iban_ron",
        label: "IBAN (RON)",
        icon: "ri-bank-card-line",
      },
      {
        id: "iban_euro",
        name: "iban_euro",
        label: "IBAN (EUR)",
        icon: "ri-bank-card-line",
      },
    ];

    const getFieldsForTab = () => {
      switch (activeTab) {
        case TABS.PERSONAL_INFO:
          return companyFields;
        case TABS.CONTACT:
          return contactFields;
        case TABS.ADDRESS:
          return addressFields;
        case TABS.BANKING:
          return bankingFields;
        case TABS.ADDITIONAL_CONTACT:
          return null;
        default:
          return [];
      }
    };

    const fields = getFieldsForTab();

    if (activeTab === TABS.ADDITIONAL_CONTACT) {
      return renderAdditionalContactTab();
    }

    return (
      <Row>
        {fields?.map((field) => (
          <Col md={field.type === "textarea" ? 12 : 6} key={field.id}>
            {renderFormField(field)}
          </Col>
        ))}
      </Row>
    );
  }, [activeTab, renderFormField]);

  const renderAdditionalContactTab = useCallback(() => {
    if (displayAdditionalContact) {
      return (
        <Row>
          <Col md={6}>
            <div className="mb-3">
              <Label>Name</Label>
              <Input
                type="text"
                value={ displayAdditionalContact.display_name || ""
                }
                disabled={true}
              />
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label>Email</Label>
              <Input
                type="text"
                value={
                  displayAdditionalContact.email || ""
                }
                disabled={true}
              />
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label>Phone</Label>
              <Input
                type="text"
                value={displayAdditionalContact.phone_number || ""}
                disabled={true}
              />
            </div>
          </Col>
          <Col md={12}>
            <div className="mb-3 d-flex justify-content-between">
              <Button
                color="danger"
                size="sm"
                onClick={toggleUnlinkModal}
                disabled={isViewer || isContactOperationPending}
              >
                {isContactOperationPending ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-link-off me-1"></i> Unlink Contact
                  </>
                )}
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={handleAdditionalContactClick}
                disabled={isContactOperationPending}
              >
                <i className="mdi mdi-account-details me-1"></i> View Client
                Details
              </Button>
            </div>
          </Col>
        </Row>
      );
    }

    return (
      <div className="text-center py-4">
        <p className="text-muted">
          No additional contact information available
        </p>
        {!isViewer && (
          <div className="mt-3" style={{ maxWidth: "300px", margin: "0 auto" }}>
            <div className="d-flex flex-column gap-3">
              <div>
                <Label for="additional_contact1">Link Existing Contact</Label>
                <Input
                  type="select"
                  id="additional_contact1"
                  name="additional_contact1"
                  value={formData?.additional_contact1 || ""}
                  onChange={handleInputChange}
                  className="border-primary mb-2"
                  disabled={isContactOperationPending}
                >
                  <option value="">Select a contact</option>
                  {availableContacts.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.display_name || ""}
                    </option>
                  ))}
                </Input>
                {formData?.additional_contact1 && (
                  <div className="w-100 mt-2">
                    <Button
                      color="success"
                      onClick={handleAddExistingContact}
                      className="w-100"
                      size="sm"
                      disabled={isContactOperationPending}
                    >
                      {isContactOperationPending ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          Linking...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-1"></i>
                          Link Contact
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    displayAdditionalContact,
    formData,
    isEditing,
    isViewer,
    isContactOperationPending,
    handleInputChange,
    toggleUnlinkModal,
    handleAdditionalContactClick,
    availableContacts,
    handleAddExistingContact,
  ]);

  // Loading and error states (rest remains the same)
  if (isLoadingDifferentClient) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Spinner color="primary" />
            <span className="ms-2">Loading client details...</span>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line fs-1 text-danger mb-3 d-block"></i>
            <h5>Error Loading Client</h5>
            <p className="text-muted">{error}</p>
            <Button
              color="primary"
              onClick={() => dispatch(onGetClientDetails(id))}
            >
              Try Again
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (!clientInfo) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-user-line fs-1 text-muted mb-3 d-block"></i>
            <h5>No Client Data Found</h5>
            <p className="text-muted">
              The requested client could not be found.
            </p>
            <Button color="primary" onClick={() => navigate("/clients")}>
              Back to Clients
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <Container fluid>
        {/* Page Header */}
        <div className="page-header mb-4">
          <h4 className="mb-1">
            {clientDisplayName}
            <Badge
              color={isNaturalPerson ? "info" : "warning"}
              className="ms-2"
              pill
            >
              {isNaturalPerson ? "Individual" : "Company"}
            </Badge>
          </h4>
          <p className="text-muted mb-0">{clientSubtitle}</p>
        </div>

        <Row className="g-4">
          <Col>
            {/* Client Information Card */}
            <Card className="mb-4 border-0 shadow-lg card-hover">
              <CardHeader className="bg-transparent border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <span className="text-white shadow-sm bg-primary icon-circle me-2">
                      <i
                        className={`${
                          isNaturalPerson
                            ? "ri-user-3-line"
                            : "ri-building-line"
                        }`}
                      ></i>
                    </span>
                    <h5 className="mb-0">
                      {isNaturalPerson
                        ? "Personal Information"
                        : "Company Information"}
                    </h5>
                  </div>
                  {!isViewer && (
                    <div>
                      <Button
                        color={isEditing ? "secondary" : "primary"}
                        size="sm"
                        onClick={toggleEdit}
                        className="me-2"
                      >
                        <i
                          className={`ri-${
                            isEditing ? "close-line" : "pencil-line"
                          } me-1`}
                        ></i>
                        {isEditing ? "Cancel" : "Edit"}
                      </Button>
                      {isEditing && (
                        <Button color="success" size="sm" onClick={handleSave}>
                          <i className="ri-save-line me-1"></i>Save
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardBody className="p-4">
                {/* Navigation Tabs */}
                <Nav tabs className="nav-tabs-custom nav-justified mb-4">
                  <NavItem>
                    <NavLink
                      className={
                        activeTab === TABS.PERSONAL_INFO ? "active" : ""
                      }
                      onClick={() => toggleTab(TABS.PERSONAL_INFO)}
                      style={{ cursor: "pointer" }}
                    >
                      <i
                        className={`${
                          isNaturalPerson
                            ? "ri-user-3-line"
                            : "ri-building-line"
                        } me-1`}
                      ></i>
                      {isNaturalPerson ? "Personal Info" : "Company Info"}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === TABS.CONTACT ? "active" : ""}
                      onClick={() => toggleTab(TABS.CONTACT)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-contacts-line me-1"></i>
                      Contact
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === TABS.ADDRESS ? "active" : ""}
                      onClick={() => toggleTab(TABS.ADDRESS)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-map-pin-line me-1"></i>
                      Address
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === TABS.BANKING ? "active" : ""}
                      onClick={() => toggleTab(TABS.BANKING)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-bank-line me-1"></i>
                      Banking
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={
                        activeTab === TABS.ADDITIONAL_CONTACT ? "active" : ""
                      }
                      onClick={() => toggleTab(TABS.ADDITIONAL_CONTACT)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-user-follow-line me-1"></i>
                      Additional Contact
                    </NavLink>
                  </NavItem>
                </Nav>

                {/* Tab Content */}
                <TabContent activeTab={activeTab}>
                  <TabPane tabId={activeTab}>
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        {isNaturalPerson
                          ? renderNaturalPersonForm()
                          : renderLegalEntityForm()}
                      </div>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>

            {/* Contracts Card */}
            <Card className="border-0 shadow-lg card-hover">
              <CardHeader className="bg-transparent border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <span
                      className="text-white shadow-sm icon-circle me-2"
                      style={{ backgroundColor: "#4b38b3" }}
                    >
                      <i className="ri-file-list-3-line"></i>
                    </span>
                    <h5 className="mb-0">
                      Contracts{" "}
                      <span
                        className="badge rounded-pill ms-2"
                        style={{ backgroundColor: "#4b38b3" }}
                      >
                        {contracts &&
                          contracts.length > 0 &&
                          `${contracts.length}`}
                      </span>
                    </h5>
                  </div>
                  {!isViewer && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleContractModal}
                    >
                      <i className="ri-add-line me-1"></i>Create Contract
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardBody className="p-4">
                <ClientContracts contracts={contracts} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modals */}
        <ContractModal
          show={contractModal}
          onCloseClick={handleCloseContractModal}
          clientDetails={clientInfo}
        />

        <Modal isOpen={unlinkModalOpen} toggle={toggleUnlinkModal} centered>
          <ModalHeader toggle={toggleUnlinkModal}>
            Unlink Additional Contact
          </ModalHeader>
          <ModalBody>
            <div className="text-center">
              <i className="ri-user-unfollow-line fs-1 text-warning mb-3 d-block"></i>
              <p>
                Are you sure you want to unlink this contact? This action cannot
                be undone.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={toggleUnlinkModal}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={handleUnlinkAdditionalContact}
              disabled={isContactOperationPending}
            >
              {isContactOperationPending ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Unlinking...
                </>
              ) : (
                <>
                  <i className="ri-delete-bin-line me-1"></i>Unlink
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        <style jsx>{`
          .icon-circle {
            width: 40px !important;
            height: 40px !important;
            min-width: 40px;
            min-height: 40px;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            border-radius: 50% !important;
            flex-shrink: 0;
          }

          .icon-circle i {
            font-size: 18px !important;
            line-height: 1 !important;
          }

          .card-hover {
            transition: all 0.3s ease;
            border-radius: 12px !important;
          }

          .card-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
          }

          .nav-tabs-custom .nav-item .nav-link.active {
            color: #4b38b3 !important;
            border-bottom: 2px solid #4b38b3 !important;
            background: transparent !important;
          }

          .nav-tabs-custom .nav-item .nav-link {
            border: none !important;
            border-bottom: 2px solid transparent !important;
            background: transparent !important;
          }

          .nav-tabs-custom .nav-item .nav-link:hover {
            border-bottom: 2px solid #dee2e6 !important;
          }
        `}</style>
      </Container>
    </div>
  );
};

export default ClientDetail;
